import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PagedList } from '../models/paged-list.interface';
import { RequestModel } from '../models/request-model.interface';
import { CompanyShipment } from '../models/sales/company-shipment.interface';
import { CompanyShipmentDetails } from '../models/sales/company-shipment-details.interface';
import { CreateShipmentStandaloneRequest } from '../models/sales/create-shipment-standalone-request.interface';

/**
 * Top-level standalone shipments service bound to `/api/companies/{companyId}/shipments`.
 * The linked-mode service `SalesOrderShipmentService` serves the per-order tab; this
 * service powers the top-level shipments page and create modal.
 */
@Injectable({
  providedIn: 'root',
})
export class ShipmentService {
  private readonly httpClient = inject(HttpClient);

  public getShipmentsPaged(
    companyId: string,
    request: RequestModel,
  ): Observable<PagedList<CompanyShipment>> {
    return this.httpClient.post<PagedList<CompanyShipment>>(
      `${this.getShipmentsUrl(companyId)}/paged`,
      request,
    );
  }

  public getShipments(companyId: string): Observable<CompanyShipment[]> {
    return this.httpClient.get<CompanyShipment[]>(this.getShipmentsUrl(companyId));
  }

  public getShipmentById(
    companyId: string,
    shipmentId: string,
  ): Observable<CompanyShipmentDetails> {
    return this.httpClient.get<CompanyShipmentDetails>(
      `${this.getShipmentsUrl(companyId)}/${shipmentId}`,
    );
  }

  public createShipment(
    companyId: string,
    body: CreateShipmentStandaloneRequest,
  ): Observable<CompanyShipmentDetails> {
    return this.httpClient.post<CompanyShipmentDetails>(this.getShipmentsUrl(companyId), body);
  }

  public updateShipment(
    companyId: string,
    shipmentId: string,
    body: { status?: 'Shipped'; shipmentDate?: string },
  ): Observable<CompanyShipmentDetails> {
    return this.httpClient.patch<CompanyShipmentDetails>(
      `${this.getShipmentsUrl(companyId)}/${shipmentId}`,
      body,
    );
  }

  /**
   * Convenience wrapper for the canonical `Packed -> Shipped` PATCH transition.
   * Backend triggers stock decrement on this transition; surface it as its own method
   * so consumers do not need to remember the wire constant.
   */
  public markShipped(
    companyId: string,
    shipmentId: string,
  ): Observable<CompanyShipmentDetails> {
    return this.updateShipment(companyId, shipmentId, { status: 'Shipped' });
  }

  public deleteShipment(companyId: string, shipmentId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.getShipmentsUrl(companyId)}/${shipmentId}`);
  }

  private getShipmentsUrl(companyId: string): string {
    return `${environment.apiUrl}/companies/${companyId}/shipments`;
  }
}
